/**
 * Observability console.
 *
 * Four questions, one screen: is it fast, is it failing, is a policy refusing
 * people, and is anyone being served state that didn't come from their record.
 * Everything here reads the same `ALERT_RULES` the scheduled alert job uses, so
 * a green board means nothing is about to page anyone.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Area, AreaChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ALERT_RULES, evaluateAlerts, type AlertVerdict, type HealthRow,
} from "@/lib/observability";
import {
  Activity, AlertTriangle, ArrowLeft, CheckCircle2, Gauge, Loader2, ShieldAlert, RefreshCw,
} from "lucide-react";

type Bucket = {
  bucket_start: string;
  events: number;
  failures: number;
  denials: number;
  hydration_fallbacks: number;
  p95_ms: number | null;
};

/** One client-side crash, with the context that makes it reproducible. */
type CrashRow = {
  id: string;
  created_at: string;
  error_code: string | null;
  error_message: string | null;
  path: string | null;
  props: Record<string, unknown> | null;
};

const WINDOWS = [
  { label: "15m", minutes: 15 },
  { label: "1h", minutes: 60 },
  { label: "24h", minutes: 1440 },
  { label: "7d", minutes: 10080 },
];

const AdminObservability = () => {
  const [minutes, setMinutes] = useState(60);
  const [rows, setRows] = useState<HealthRow[]>([]);
  const [series, setSeries] = useState<Bucket[]>([]);
  const [crashes, setCrashes] = useState<CrashRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const since = new Date(Date.now() - minutes * 60_000).toISOString();
    const [health, ts, crash] = await Promise.all([
      supabase.rpc("get_ops_health", { p_minutes: minutes }),
      supabase.rpc("get_ops_timeseries", { p_minutes: minutes, p_buckets: 24 }),
      // The crash rows themselves, not just their count: the route, the inputs
      // and the component stack are the whole point of capturing them.
      supabase
        .from("ops_events")
        .select("id, created_at, error_code, error_message, path, props")
        .eq("name", "client_error")
        .gte("created_at", since)
        .order("created_at", { ascending: false })
        .limit(25),
    ]);
    if (health.error) setError(health.error.message);
    setRows((health.data ?? []) as HealthRow[]);
    setSeries(((ts.data ?? []) as Bucket[]).map((b) => ({ ...b, p95_ms: b.p95_ms ?? 0 })));
    setCrashes((crash.data ?? []) as CrashRow[]);
    setLoading(false);
  }, [minutes]);

  useEffect(() => { void load(); }, [load]);

  // Auto-refresh: a console you have to remember to reload is a console nobody
  // is looking at when it matters.
  useEffect(() => {
    const id = setInterval(() => { void load(); }, 60_000);
    return () => clearInterval(id);
  }, [load]);

  const verdicts = evaluateAlerts(rows);
  const firing = verdicts.filter((v) => v.firing);
  const totals = rows.reduce(
    (acc, r) => ({
      events: acc.events + Number(r.events || 0),
      failures: acc.failures + Number(r.failures || 0),
      denials: acc.denials + Number(r.denials || 0),
      fallbacks: acc.fallbacks + Number(r.hydration_fallbacks || 0),
      p95: Math.max(acc.p95, Number(r.p95_ms || 0)),
    }),
    { events: 0, failures: 0, denials: 0, fallbacks: 0, p95: 0 },
  );
  const errorRate = totals.events ? (totals.failures / totals.events) * 100 : 0;

  const runScan = async () => {
    setScanning(true);
    setScanResult(null);
    const { data, error: fnErr } = await supabase.functions.invoke("ops-alerts", { body: { minutes } });
    setScanning(false);
    if (fnErr) { setScanResult(`Scan failed: ${fnErr.message}`); return; }
    const fired = (data as { fired?: unknown[] } | null)?.fired ?? [];
    setScanResult(fired.length ? `${fired.length} alert(s) dispatched.` : "All rules within threshold.");
    void load();
  };

  const chartData = series.map((b) => ({
    t: new Date(b.bucket_start).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
    events: Number(b.events || 0),
    failures: Number(b.failures || 0),
    denials: Number(b.denials || 0),
    fallbacks: Number(b.hydration_fallbacks || 0),
    p95: Number(b.p95_ms || 0),
  }));

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 border-b border-border/50 bg-background/80 backdrop-blur-xl">
        <div className="container mx-auto flex h-16 items-center justify-between px-6">
          <Link to="/admin" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4" aria-hidden /> Admin Hub
          </Link>
          <h1 className="font-display text-lg font-bold">Observability</h1>
          <div className="w-24" />
        </div>
      </header>

      <main className="container mx-auto max-w-6xl space-y-6 px-4 py-8">
        <div className="flex flex-wrap items-center gap-2">
          <div role="group" aria-label="Time window" className="flex gap-1 rounded-xl bg-muted/40 p-1">
            {WINDOWS.map((w) => (
              <Button
                key={w.minutes}
                size="sm"
                variant={w.minutes === minutes ? "default" : "ghost"}
                aria-pressed={w.minutes === minutes}
                onClick={() => setMinutes(w.minutes)}
              >
                {w.label}
              </Button>
            ))}
          </div>
          <Button size="sm" variant="outline" onClick={() => void load()} disabled={loading}>
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden /> : <RefreshCw className="mr-2 h-4 w-4" aria-hidden />}
            Refresh
          </Button>
          <Button size="sm" variant="outline" onClick={() => void runScan()} disabled={scanning}>
            {scanning ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden /> : <ShieldAlert className="mr-2 h-4 w-4" aria-hidden />}
            Run alert scan
          </Button>
          {scanResult && <span className="text-sm text-muted-foreground">{scanResult}</span>}
        </div>

        {error && (
          <p className="rounded-xl border border-destructive/40 bg-destructive/10 p-4 text-sm">
            {error}
          </p>
        )}

        {/* Headline numbers */}
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
          <Stat label="Operations" value={totals.events.toLocaleString()} icon={<Activity className="h-4 w-4" aria-hidden />} />
          <Stat label="Error rate" value={`${errorRate.toFixed(2)}%`} tone={errorRate > 5 ? "bad" : errorRate > 2 ? "warn" : "ok"} />
          <Stat label="Worst p95" value={`${Math.round(totals.p95)} ms`} tone={totals.p95 > 2500 ? "warn" : "ok"} icon={<Gauge className="h-4 w-4" aria-hidden />} />
          <Stat label="RLS denials" value={totals.denials.toLocaleString()} tone={totals.denials > 10 ? "bad" : "ok"} />
          <Stat label="Hydration fallbacks" value={totals.fallbacks.toLocaleString()} tone={totals.fallbacks > 5 ? "bad" : "ok"} />
        </div>

        {/* Alerts */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {firing.length ? <AlertTriangle className="h-5 w-5 text-destructive" aria-hidden />
                : <CheckCircle2 className="h-5 w-5 text-emerald-500" aria-hidden />}
              Alerts ({firing.length}/{ALERT_RULES.length} firing)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {verdicts.map((v) => <AlertRow key={v.rule.id} verdict={v} />)}
          </CardContent>
        </Card>

        {/* Trend */}
        <Card>
          <CardHeader><CardTitle>Volume, failures and latency over time</CardTitle></CardHeader>
          <CardContent>
            <div className="h-72" role="img" aria-label="Operations, failures, denials and p95 latency over the selected window">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border/40" />
                  <XAxis dataKey="t" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend />
                  <Area yAxisId="left" type="monotone" dataKey="events" name="Operations" stroke="hsl(var(--primary))" fill="hsl(var(--primary) / 0.18)" />
                  <Area yAxisId="left" type="monotone" dataKey="failures" name="Failures" stroke="hsl(var(--destructive))" fill="hsl(var(--destructive) / 0.2)" />
                  <Area yAxisId="left" type="monotone" dataKey="fallbacks" name="Hydration fallbacks" stroke="hsl(var(--accent))" fill="hsl(var(--accent) / 0.2)" />
                  <Area yAxisId="right" type="monotone" dataKey="p95" name="p95 ms" stroke="hsl(var(--muted-foreground))" fill="transparent" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Per-area */}
        <Card>
          <CardHeader><CardTitle>By area</CardTitle></CardHeader>
          <CardContent>
            {rows.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No telemetry in this window yet. Walk a flow (or run the load-test suite) and refresh.
              </p>
            ) : (
              <div className="overflow-auto rounded-lg border border-border/50">
                <table className="w-full text-sm">
                  <caption className="sr-only">Latency and error rates per instrumented area</caption>
                  <thead className="bg-muted/50">
                    <tr className="text-left">
                      <th scope="col" className="px-3 py-2 font-medium">Area</th>
                      <th scope="col" className="px-3 py-2 font-medium">Ops</th>
                      <th scope="col" className="px-3 py-2 font-medium">Errors</th>
                      <th scope="col" className="px-3 py-2 font-medium">Denials</th>
                      <th scope="col" className="px-3 py-2 font-medium">Fallbacks</th>
                      <th scope="col" className="px-3 py-2 font-medium">Avg</th>
                      <th scope="col" className="px-3 py-2 font-medium">p95</th>
                      <th scope="col" className="px-3 py-2 font-medium">Max</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.area} className="border-t border-border/40">
                        <td className="px-3 py-2 font-mono text-xs">{r.area}</td>
                        <td className="px-3 py-2">{Number(r.events).toLocaleString()}</td>
                        <td className="px-3 py-2">{r.failures} ({Number(r.error_rate).toFixed(2)}%)</td>
                        <td className="px-3 py-2">{r.denials}</td>
                        <td className="px-3 py-2">{r.hydration_fallbacks}</td>
                        <td className="px-3 py-2">{r.avg_ms ?? "—"} ms</td>
                        <td className="px-3 py-2">{r.p95_ms ?? "—"} ms</td>
                        <td className="px-3 py-2">{r.max_ms ?? "—"} ms</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Client crashes — the ones that used to arrive as a blank screen. */}
        <Card>
          <CardHeader><CardTitle>Client crashes</CardTitle></CardHeader>
          <CardContent>
            {crashes.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No client-side crashes in this window. React invariants, uncaught errors and
                unhandled promise rejections all land here with their route and component stack.
              </p>
            ) : (
              <ul className="space-y-3">
                {crashes.map((c) => (
                  <li key={c.id} className="rounded-xl border border-destructive/30 bg-destructive/5 p-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant="destructive" className="font-mono text-[11px]">{c.error_code ?? "error"}</Badge>
                      <span className="font-mono text-xs text-muted-foreground">
                        {String(c.props?.route ?? c.path ?? "—")}
                        {c.props?.band ? ` · band=${String(c.props.band)}` : ""}
                      </span>
                      <span className="ml-auto text-xs text-muted-foreground">
                        {new Date(c.created_at).toLocaleString()}
                      </span>
                    </div>
                    <p className="mt-2 text-sm font-medium">{c.error_message}</p>
                    {c.props?.invariant_hint && (
                      <p className="mt-1 text-xs text-muted-foreground">{String(c.props.invariant_hint)}</p>
                    )}
                    <details className="mt-2">
                      <summary className="cursor-pointer text-xs text-muted-foreground">
                        Stack, inputs and build
                      </summary>
                      <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap rounded-lg bg-muted/40 p-2 text-[11px] leading-relaxed">
{JSON.stringify(c.props, null, 2)}
                      </pre>
                    </details>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

const Stat = ({ label, value, tone = "ok", icon }: {
  label: string; value: string; tone?: "ok" | "warn" | "bad"; icon?: React.ReactNode;
}) => (
  <div className="rounded-2xl border border-border/50 bg-card p-4">
    <div className="flex items-center gap-2 text-xs text-muted-foreground">{icon}{label}</div>
    <div className={`mt-1 text-2xl font-semibold ${
      tone === "bad" ? "text-destructive" : tone === "warn" ? "text-amber-500" : ""
    }`}>{value}</div>
  </div>
);

const AlertRow = ({ verdict }: { verdict: AlertVerdict }) => (
  <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-border/40 bg-muted/20 p-3">
    <div className="min-w-0">
      <div className="flex items-center gap-2">
        <Badge variant={verdict.firing ? "destructive" : "outline"}>
          {verdict.firing ? "Firing" : "OK"}
        </Badge>
        <span className="font-medium">{verdict.rule.label}</span>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        {verdict.firing ? verdict.rule.hint : `Threshold ${verdict.rule.threshold} · needs ${verdict.rule.minEvents}+ ops`}
      </p>
    </div>
    <div className="text-right text-sm">
      <div className="font-mono">{verdict.value}</div>
      <div className="text-xs text-muted-foreground">{verdict.events.toLocaleString()} ops</div>
    </div>
  </div>
);

export default AdminObservability;
